import type { CurrentUserResponse } from '@/api/generated/models/CurrentUserResponse';
import { AuthService } from '@/api/generated/services/AuthService';
import { unwrapServiceResponse } from '@/api/envelope';
import { clearCsrfToken, requireCsrfTokenParameter, setCsrfToken } from './csrf';
import type { AuthSession, CurrentUser, LoginCredentials } from './types';

function mapCurrentUser(payload: CurrentUserResponse): CurrentUser {
  return {
    userId: payload.user_id,
    username: payload.username,
    displayName: payload.display_name,
    roles: payload.roles,
    capabilities: payload.capabilities,
  };
}

function mapAuthSession(payload: CurrentUserResponse): AuthSession {
  return {
    currentUser: mapCurrentUser(payload),
    csrfToken: payload.csrf_token,
  };
}

async function resolveAuthSession(promise: PromiseLike<unknown>): Promise<AuthSession> {
  const payload = (await unwrapServiceResponse(promise)) as CurrentUserResponse;
  const session = mapAuthSession(payload);
  setCsrfToken(session.csrfToken);
  return session;
}

export const authApi = {
  async me(): Promise<AuthSession> {
    return resolveAuthSession(AuthService.meApiV1AuthMeGet());
  },

  async login(credentials: LoginCredentials): Promise<AuthSession> {
    return resolveAuthSession(
      AuthService.loginApiV1AuthLoginPost({
        username: credentials.username,
        password: credentials.password,
        remember_me: credentials.rememberMe,
      }),
    );
  },

  async logout(): Promise<void> {
    await unwrapServiceResponse(AuthService.logoutApiV1AuthLogoutPost(requireCsrfTokenParameter()));
    clearCsrfToken();
  },
};
